import { ReactNode, useId, useState } from 'react';

import { cn } from './cn';
import { Popover, menuRow } from './Popover';
import { inputCls } from './form';
import { Check, Plus, Search } from './icons';

export type SelectOption = {
  value: string;
  label: string;
  /** Leading visual (status dot / priority chevron / member avatar); centred in a fixed 20px slot
   *  so every row's label starts at the same x, whatever the token's own size. */
  leading?: ReactNode;
  /** Extra text folded into the search match (e.g. a member's email). */
  keywords?: string;
  /** Row text tone; defaults to `text-fg-soft`. Use `text-fg-muted` for an "unset" row. */
  tone?: string;
};

/**
 * The single searchable option-picker shared by every status / priority / assignee / PdM / parent
 * control across the app (kanban cards, table rows, the item drawer). Callers supply only the
 * `trigger` (a card's icon button, or the drawer's full-width row) and the option list; the popover
 * body — the type-to-filter search, the {@link menuRow} options with their leading token and a
 * trailing {@link Check}, keyboard operability, and focus management — is identical everywhere, so
 * editing a value feels the same on the board and in the drawer.
 *
 * The search is a FIXED header sitting above an inner scroll region (not a sticky header inside the
 * popover's own padded scroll box, which leaves a gap where scrolled rows peek through the top).
 */
export function SelectPopover(props: {
  options: SelectOption[];
  /** Currently selected value (`''`/`null` = the unset option, if present). */
  value: string | null;
  onChange: (value: string) => void;
  trigger: (arg: { open: boolean; toggle: () => void }) => ReactNode;
  align?: 'left' | 'right';
  /** Force the search box on/off. Defaults to on whenever there's more than one option. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Shown when the search matches nothing (receives the query). */
  emptyText?: (query: string) => string;
  /** When set, a "create" action row is offered at the bottom while the typed query is non-empty
   *  and matches no existing option label — for quickly adding a new option in-place (e.g. a new
   *  epic). Receives the trimmed query; the popover then closes. */
  onCreate?: (query: string) => void;
  /** Label for the create row (defaults to `Create "<query>"`). */
  createLabel?: (query: string) => string;
  panelClassName?: string;
}) {
  const { options, value, onChange, trigger, align = 'left', searchable, searchPlaceholder = 'Search…', emptyText, onCreate, createLabel, panelClassName } = props;
  const [q, setQ] = useState('');
  const listId = useId();
  // Show the search box when there's more than one option OR a create action is offered — so a
  // user can type on a single-option list (e.g. only "None") to reach the "Create …" row.
  const showSearch = searchable ?? (options.length > 1 || !!onCreate);
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? options.filter((o) => `${o.label} ${o.keywords ?? ''}`.toLowerCase().includes(needle))
    : options;
  const current = value ?? '';
  // Offer the create row only when the query names something not already present (case-insensitive
  // exact match on a label), so "type an existing name" selects rather than duplicates.
  const showCreate = !!onCreate && !!needle && !options.some((o) => o.label.trim().toLowerCase() === needle);

  return (
    <Popover
      portal
      menu
      align={align}
      panelClassName={panelClassName}
      trigger={({ open, toggle }) =>
        trigger({
          open,
          // Clear the filter on each open so a picker always starts from the full list (an
          // outside-click / Escape dismiss bypasses the per-option handlers that reset it).
          toggle: () => {
            if (!open) setQ('');
            toggle();
          },
        })
      }
    >
      {(close) => {
        // A listbox of options (each a real, focusable <button role="option"> so the shared
        // roving-focus keyboard model in Popover keeps working); when searchable it's paired with
        // a combobox input that controls it. Physical focus roves the options — not
        // aria-activedescendant — matching Popover's focus machinery.
        const list = (
          <div
            role="listbox"
            id={listId}
            className={showSearch ? 'thin-scroll -mx-1.5 max-h-[240px] overflow-auto px-1.5' : undefined}
          >
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={current === o.value}
                onClick={() => {
                  if (o.value !== current) onChange(o.value);
                  setQ('');
                  close();
                }}
                className={cn(menuRow, o.tone ?? 'text-fg-soft')}
              >
                {o.leading != null ? <span className="flex h-5 w-5 shrink-0 items-center justify-center">{o.leading}</span> : null}
                <span className="flex-1 truncate">{o.label}</span>
                {current === o.value ? <Check className="text-accent" /> : null}
              </button>
            ))}
            {filtered.length === 0 && !showCreate ? (
              <div className="px-2 py-6 text-center text-[12px] text-fg-ghost">
                {emptyText ? emptyText(q.trim()) : 'No matches'}
              </div>
            ) : null}
            {showCreate ? (
              <button
                type="button"
                onClick={() => {
                  onCreate?.(q.trim());
                  setQ('');
                  close();
                }}
                className={cn(menuRow, 'text-accent')}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <Plus />
                </span>
                <span className="flex-1 truncate">
                  {(createLabel ?? ((query) => `Create “${query}”`))(q.trim())}
                </span>
              </button>
            ) : null}
          </div>
        );
        if (!showSearch) return list;
        return (
          <>
            <div className="-mx-1.5 -mt-1.5 mb-1 border-b border-line p-1.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-faint" />
                <input
                  role="combobox"
                  aria-expanded
                  aria-controls={listId}
                  aria-autocomplete="list"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  className={cn(inputCls, 'h-9 pl-8')}
                />
              </div>
            </div>
            {list}
          </>
        );
      }}
    </Popover>
  );
}

export default SelectPopover;
